import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminRawThoughtForm from './AdminRawThoughtForm';
import AdminRawThoughtCard from './AdminRawThoughtCard';
import AdminRawThoughtTimeline from './AdminRawThoughtTimeline';
import AdminArticleForm from './AdminArticleForm';
import './AdminRawThoughtManager.css';

const AdminRawThoughtManager = () => {
    const [thoughts, setThoughts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [viewMode, setViewMode] = useState('card'); // 'card' or 'timeline'
    const [selectedThoughts, setSelectedThoughts] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [editingThought, setEditingThought] = useState(null);
    const [filters, setFilters] = useState({
        search: '',
        category: '',
        startDate: '',
        endDate: ''
    });

    // New state for article conversion
    const [isConverting, setIsConverting] = useState(false);
    const [isArticleFormOpen, setIsArticleFormOpen] = useState(false);
    const [aiGeneratedArticleData, setAiGeneratedArticleData] = useState(null);

    // Add modal for AI chapter generation
    const [isChapterModalOpen, setIsChapterModalOpen] = useState(false);
    const [chapterModalThought, setChapterModalThought] = useState(null);
    const [aiChapterOptions, setAiChapterOptions] = useState([]);
    const [isGeneratingChapters, setIsGeneratingChapters] = useState(false);
    const [selectedChapterOption, setSelectedChapterOption] = useState(null);
    const [editChapterTitle, setEditChapterTitle] = useState('');
    const [editChapterContent, setEditChapterContent] = useState('');

    // Add state for article options modal
    const [isArticleOptionsModalOpen, setIsArticleOptionsModalOpen] = useState(false);
    const [articleOptions, setArticleOptions] = useState([]);
    const [selectedArticleOption, setSelectedArticleOption] = useState(null);
    
    // State for viewing full article content
    const [isViewFullModalOpen, setIsViewFullModalOpen] = useState(false);
    const [viewingArticle, setViewingArticle] = useState(null);

    useEffect(() => {
        fetchThoughts();
        fetchCategories();
    }, [filters]);

    const fetchThoughts = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('app_token');
            const params = new URLSearchParams();
            
            if (filters.search) params.append('search', filters.search);
            if (filters.category) params.append('category', filters.category);
            if (filters.startDate) params.append('startDate', filters.startDate);
            if (filters.endDate) params.append('endDate', filters.endDate);

            const response = await axios.get(`/api/raw-thoughts?${params}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setThoughts(response.data);
        } catch (err) {
            setError('Failed to fetch raw thoughts');
            console.error('Error fetching thoughts:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const token = localStorage.getItem('app_token');
            const response = await axios.get('/api/raw-thoughts/categories', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCategories(response.data);
        } catch (err) {
            console.error('Error fetching categories:', err);
        }
    };

    const handleCreateThought = async (thoughtData) => {
        try {
            const token = localStorage.getItem('app_token');
            const response = await axios.post('/api/raw-thoughts', thoughtData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setThoughts(prev => [response.data, ...prev]);
            setShowForm(false);
        } catch (err) {
            setError('Failed to create raw thought');
            console.error('Error creating thought:', err);
        }
    };

    const handleUpdateThought = async (id, updateData) => {
        try {
            const token = localStorage.getItem('app_token');
            const response = await axios.put(`/api/raw-thoughts/${id}`, updateData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setThoughts(prev => prev.map(thought => 
                thought.id === id ? response.data : thought
            ));
            setEditingThought(null);
        } catch (err) {
            setError('Failed to update raw thought');
            console.error('Error updating thought:', err);
        }
    };

    const handleDeleteThought = async (id) => {
        if (!window.confirm('Are you sure you want to delete this thought?')) return;
        
        try {
            const token = localStorage.getItem('app_token');
            await axios.delete(`/api/raw-thoughts/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setThoughts(prev => prev.filter(thought => thought.id !== id));
        } catch (err) {
            setError('Failed to delete raw thought');
            console.error('Error deleting thought:', err);
        }
    };

    const handleConvertToArticle = async (thought) => {
        setIsConverting(true);
        try {
            const token = localStorage.getItem('app_token');
            const response = await axios.post('/api/raw-thoughts/convert-to-article', { thoughtIds: [thought.id] }, { headers: { Authorization: `Bearer ${token}` } });
            
            if (response.data.options && Array.isArray(response.data.options)) {
                // Show options modal
                setArticleOptions(response.data.options);
                setIsArticleOptionsModalOpen(true);
            } else {
                // Fallback to old format
                setAiGeneratedArticleData(response.data.articleData);
                setIsArticleFormOpen(true);
            }
        } catch (err) {
            setError('Failed to convert thought to article');
        }
        setIsConverting(false);
    };

    const handleSelectArticleOption = (option) => {
        setSelectedArticleOption(option);
        setAiGeneratedArticleData(option);
        setIsArticleFormOpen(true);
        setIsArticleOptionsModalOpen(false);
    };

    const handleViewFullArticle = (option, e) => {
        e.stopPropagation(); // Prevent triggering the select function
        setViewingArticle(option);
        setIsViewFullModalOpen(true);
    };

    const handleArticleSave = async (formData) => {
        try {
            const token = localStorage.getItem('app_token');
            const config = { headers: { Authorization: `Bearer ${token}` } };
            
            await axios.post('/api/admin/articles', formData, config);
            setIsArticleFormOpen(false);
            setAiGeneratedArticleData(null);
            // Optionally refresh thoughts or show success message
        } catch (err) {
            setError('Failed to save article');
            console.error('Error saving article:', err);
        }
    };

    const handleArticleCancel = () => {
        setIsArticleFormOpen(false);
        setAiGeneratedArticleData(null);
    };

    const handleThoughtSelect = (id) => {
        setSelectedThoughts(prev => 
            prev.includes(id) 
                ? prev.filter(thoughtId => thoughtId !== id)
                : [...prev, id]
        );
    };

    const handleSelectAll = () => {
        if (selectedThoughts.length === thoughts.length) {
            setSelectedThoughts([]);
        } else {
            setSelectedThoughts(thoughts.map(thought => thought.id));
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const clearFilters = () => {
        setFilters({
            search: '',
            category: '',
            startDate: '',
            endDate: ''
        });
    };

    const handleThoughtEdit = (thought) => {
        // Ensure the thought object is valid before setting it for editing
        if (thought && typeof thought === 'object') {
            setEditingThought(thought);
        } else {
            setError('Invalid thought data for editing');
        }
    };

    const handleConvertToChapter = (thought) => {
        setChapterModalThought(thought);
        setIsChapterModalOpen(true);
        setAiChapterOptions([]);
        setSelectedChapterOption(null);
        setEditChapterTitle('');
        setEditChapterContent('');
    };

    const handleGenerateChapters = async () => {
        setIsGeneratingChapters(true);
        setAiChapterOptions([]);
        setSelectedChapterOption(null);
        setEditChapterTitle('');
        setEditChapterContent('');
        try {
            const token = localStorage.getItem('app_token');
            // Assume only one book, get its id from somewhere (e.g., window.bookId or fetch it)
            const bookId = window.bookId || 1;
            const res = await axios.post(`/api/admin/books/${bookId}/ai-chapter-from-raw-thought`, { rawThought: chapterModalThought.content }, { headers: { Authorization: `Bearer ${token}` } });
            setAiChapterOptions(res.data.options || []);
        } catch (err) {
            setError('Failed to generate chapters');
        }
        setIsGeneratingChapters(false);
    };

    const handleSelectChapterOption = (option) => {
        setSelectedChapterOption(option);
        setEditChapterTitle(option.title);
        setEditChapterContent(option.content);
    };

    const handleAddChapterToBook = async () => {
        try {
            const token = localStorage.getItem('app_token');
            const bookId = window.bookId || 1;
            // Fetch current chapters
            const bookRes = await axios.get('/api/admin/books', { headers: { Authorization: `Bearer ${token}` } });
            let book = bookRes.data[0];
            let chapters = book.chapters;
            if (typeof chapters === 'string') {
                try { chapters = JSON.parse(chapters); } catch { chapters = []; }
            }
            chapters = [...(chapters || []), { id: Date.now(), title: editChapterTitle, order: (chapters?.length || 0) + 1, content: editChapterContent }];
            await axios.put(`/api/admin/books/${bookId}`, { chapters }, { headers: { Authorization: `Bearer ${token}` } });
            setIsChapterModalOpen(false);
            setChapterModalThought(null);
            setAiChapterOptions([]);
            setSelectedChapterOption(null);
            setEditChapterTitle('');
            setEditChapterContent('');
        } catch (err) {
            setError('Failed to add chapter to book');
        }
    };

    if (loading) return <div className="loading">Loading raw thoughts...</div>;

    return (
        <div className="raw-thoughts-manager">
            <div className="manager-header">
                <h1>Raw Thoughts Manager</h1>
                <div className="header-actions">
                    <button 
                        className="btn-primary" 
                        onClick={() => setShowForm(true)}
                    >
                        <span className="material-icons">add</span>
                        New Thought
                    </button>
                </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            {/* Filters */}
            <div className="filters-section">
                <div className="filters-row">
                    <div className="filter-group">
                        <input
                            type="text"
                            placeholder="Search thoughts..."
                            value={filters.search}
                            onChange={(e) => handleFilterChange('search', e.target.value)}
                            className="search-input"
                        />
                    </div>
                    <div className="filter-group">
                        <select
                            value={filters.category}
                            onChange={(e) => handleFilterChange('category', e.target.value)}
                            className="filter-select"
                        >
                            <option value="">All Categories</option>
                            {categories.map(category => (
                                <option key={category} value={category}>{category}</option>
                            ))}
                        </select>
                    </div>
                    <div className="filter-group">
                        <input
                            type="date"
                            value={filters.startDate}
                            onChange={(e) => handleFilterChange('startDate', e.target.value)}
                            className="date-input"
                            placeholder="From"
                            aria-label="From"
                        />
                    </div>
                    <div className="filter-group">
                        <input
                            type="date"
                            value={filters.endDate}
                            onChange={(e) => handleFilterChange('endDate', e.target.value)}
                            className="date-input"
                            placeholder="To"
                            aria-label="To"
                        />
                    </div>
                    <button onClick={clearFilters} className="btn-secondary">
                        Clear Filters
                    </button>
                </div>
            </div>

            {/* View Mode Toggle */}
            <div className="view-controls">
                <div className="view-toggle">
                    <button
                        className={`view-btn ${viewMode === 'card' ? 'active' : ''}`}
                        onClick={() => setViewMode('card')}
                    >
                        <span className="material-icons">grid_view</span>
                        Card View
                    </button>
                    <button
                        className={`view-btn ${viewMode === 'timeline' ? 'active' : ''}`}
                        onClick={() => setViewMode('timeline')}
                    >
                        <span className="material-icons">timeline</span>
                        Timeline
                    </button>
                </div>
            </div>

            {/* Content */}
            {thoughts.length === 0 ? (
                <div className="empty-state">
                    <span className="material-icons">lightbulb_outline</span>
                    <h3>No raw thoughts found</h3>
                    <p>Create your first raw thought to get started</p>
                    <button 
                        className="btn-primary" 
                        onClick={() => setShowForm(true)}
                    >
                        Create First Thought
                    </button>
                </div>
            ) : (
                <div className="thoughts-content">
                    {viewMode === 'card' ? (
                        <div className="thoughts-grid">
                            {thoughts.map(thought => (
                                <AdminRawThoughtCard
                                    key={thought.id}
                                    thought={thought}
                                    onEdit={handleThoughtEdit}
                                    onDelete={handleDeleteThought}
                                    onConvertToChapter={handleConvertToChapter}
                                    onConvertToArticle={handleConvertToArticle}
                                />
                            ))}
                        </div>
                    ) : (
                        <AdminRawThoughtTimeline
                            thoughts={thoughts}
                            onEdit={handleThoughtEdit}
                            onDelete={handleDeleteThought}
                            onConvertToChapter={handleConvertToChapter}
                            onConvertToArticle={handleConvertToArticle}
                        />
                    )}
                </div>
            )}

            {/* Forms */}
            {showForm && (
                <AdminRawThoughtForm
                    onSave={handleCreateThought}
                    onCancel={() => setShowForm(false)}
                />
            )}

            {editingThought && (
                <AdminRawThoughtForm
                    thought={editingThought}
                    onSave={(data) => handleUpdateThought(editingThought.id, data)}
                    onCancel={() => setEditingThought(null)}
                />
            )}

            {isArticleFormOpen && (
                <AdminArticleForm
                    articleId={null}
                    initialData={aiGeneratedArticleData}
                    onSave={handleArticleSave}
                    onCancel={handleArticleCancel}
                />
            )}

            {isChapterModalOpen && (
                <div className="modal-overlay">
                    <div className="modal floating-modal" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
                        <h3>Convert Raw Thought to Chapter</h3>
                        <div className="minimal-modal-section">
                            {/* Only show raw thought preview if options are not generated yet */}
                            {aiChapterOptions.length === 0 && (
                                <>
                                    <label>Raw Thought</label>
                                    <div className="raw-thought-preview" style={{ maxHeight: '200px', overflowY: 'auto' }}>{chapterModalThought?.content}</div>
                                    <button className="btn-primary" onClick={handleGenerateChapters} disabled={isGeneratingChapters} style={{ margin: '1rem 0' }}>
                                        {isGeneratingChapters ? 'Generating...' : 'Generate Chapters'}
                                    </button>
                                </>
                            )}
                            {/* Show AI chapter options after generation */}
                            {aiChapterOptions.length > 0 && (
                                <>
                                    <div className="ai-chapter-options" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                        {aiChapterOptions.map((option, idx) => (
                                            <div key={idx} className={`ai-chapter-option${selectedChapterOption === option ? ' selected' : ''}`} onClick={() => handleSelectChapterOption(option)} style={{ border: '1px solid #ccc', borderRadius: 8, padding: 12, marginBottom: 8, cursor: 'pointer', background: selectedChapterOption === option ? '#f0f8ff' : '#fff' }}>
                                                <strong>{option.title}</strong>
                                                <div style={{ marginTop: 6 }} dangerouslySetInnerHTML={{ __html: option.content }} />
                                            </div>
                                        ))}
                                    </div>
                                    {/* Show editable selected chapter below options */}
                                    {selectedChapterOption && (
                                        <div className="edit-chapter-section" style={{ marginTop: 24 }}>
                                            <label>Edit Chapter Title</label>
                                            <input value={editChapterTitle} onChange={e => setEditChapterTitle(e.target.value)} style={{ width: '100%' }} />
                                            <label>Edit Chapter Content</label>
                                            <textarea value={editChapterContent} onChange={e => setEditChapterContent(e.target.value)} rows={8} style={{ width: '100%' }} />
                                            <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
                                                <button className="btn-primary" onClick={handleAddChapterToBook}>Add as Chapter</button>
                                                <button className="btn-secondary" onClick={() => setIsChapterModalOpen(false)}>Cancel</button>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {isArticleOptionsModalOpen && (
                <div className="modal-overlay">
                    <div className="modal floating-modal" style={{ maxHeight: '90vh', overflowY: 'auto', minWidth: 500 }}>
                        <h3>Select an AI-Generated Article Option</h3>
                        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                            {articleOptions.map((option, idx) => (
                                <div key={idx} className="ai-article-option" style={{ border: '1px solid #ccc', borderRadius: 8, padding: 12, marginBottom: 12, cursor: 'pointer', background: selectedArticleOption === option ? '#f0f8ff' : '#fff' }} onClick={() => handleSelectArticleOption(option)}>
                                    <strong>{option.title}</strong>
                                    <div style={{ margin: '8px 0', color: '#666', fontSize: 14 }}>{option.summary}</div>
                                    <div style={{ fontSize: 13, color: '#888', marginBottom: 6 }}><b>Tags:</b> {option.tags}</div>
                                    <div style={{ maxHeight: 100, overflow: 'auto', fontSize: 13, border: '1px solid #eee', padding: 6, background: '#fafbfc' }} dangerouslySetInnerHTML={{ __html: option.content?.slice(0, 600) + (option.content?.length > 600 ? '...' : '') }} />
                                    <button 
                                        onClick={(e) => handleViewFullArticle(option, e)}
                                        style={{ 
                                            marginTop: 8, 
                                            padding: '4px 8px', 
                                            fontSize: 12, 
                                            background: '#f0f8ff', 
                                            border: '1px solid #2563eb', 
                                            color: '#2563eb', 
                                            borderRadius: 4, 
                                            cursor: 'pointer' 
                                        }}
                                    >
                                        View Full Article
                                    </button>
                                </div>
                            ))}
                        </div>
                        <button className="btn-secondary" onClick={() => setIsArticleOptionsModalOpen(false)} style={{ marginTop: 16 }}>Cancel</button>
                    </div>
                </div>
            )}

            {isViewFullModalOpen && viewingArticle && (
                <div className="modal-overlay">
                    <div className="modal floating-modal" style={{ maxHeight: '90vh', overflowY: 'auto', minWidth: 600, maxWidth: '80vw' }}>
                        <h3>{viewingArticle.title}</h3>
                        <div style={{ marginBottom: 16 }}>
                            <div style={{ fontSize: 14, color: '#666', marginBottom: 8 }}><strong>Summary:</strong></div>
                            <div style={{ fontSize: 14, color: '#333', marginBottom: 12 }}>{viewingArticle.summary}</div>
                            <div style={{ fontSize: 13, color: '#888' }}><strong>Tags:</strong> {viewingArticle.tags}</div>
                        </div>
                        <div style={{ 
                            maxHeight: '60vh', 
                            overflowY: 'auto', 
                            border: '1px solid #eee', 
                            padding: 16, 
                            background: '#fafbfc',
                            borderRadius: 8,
                            lineHeight: 1.6
                        }} 
                        dangerouslySetInnerHTML={{ __html: viewingArticle.content }} 
                        />
                        <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
                            <button 
                                className="btn-primary" 
                                onClick={() => {
                                    setIsViewFullModalOpen(false);
                                    handleSelectArticleOption(viewingArticle);
                                }}
                            >
                                Select This Article
                            </button>
                            <button className="btn-secondary" onClick={() => setIsViewFullModalOpen(false)}>Close</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminRawThoughtManager; 